import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import key from 'weak-key';
import { CarouselSSRWrapper, Container, Row, Col } from '@geberit/gdds';

// styles
import styles from './product-tiles.module.scss';

// types
import { ProductTilesProps } from './product-tiles.types';

// components
import { Headline } from 'components/ContentElementsGdds/headline/headline';
import { InnerHtml } from 'components/InnerHtml/inner-html';
import { SingleTile } from 'components/Product/Tile/SingleTile';
import { SectionClosingLine } from '../../Common/section-closing-line';

// utils
import { gridSizes, buildSize, themeToGridSize } from 'utils/gridSize';
import {
  currentCatalogSelector,
  isSectionLoadedBySectionIdSelector,
  productsByProductKeysCatalogSelector,
} from 'utils/selectors/productCatalogSelectors';
import { classNameBuilder as buildClassName } from 'utils/classNameBuilder';
import hashCode from 'utils/hash';
import { fetchProducts } from 'components/Product/actions';
import { useIsDesktop, useIsTablet } from 'components/App/SizeProvider';
import { useSectionId } from 'utils/hooks/use-section-id';
import { isEmpty } from 'utils/is-empty';
import { useXy } from 'utils/hooks/use-xy';

// constants
import { Formats } from 'components/ContentElementsGdds/headline/headlines.types';

function checkIsCarousel(carousel, displayOption, products, isTablet, isDesktop, isLargeDesktop) {
  return (
    (carousel || displayOption === 'carousel') &&
    ((isLargeDesktop && products.length > 4) ||
      (isDesktop && products.length > 3) ||
      (isTablet && products.length > 2) ||
      (!isDesktop && !isTablet && products.length > 1))
  );
}

export function ProductTiles({
  title,
  subtitle,
  anchor,
  contentIndex,
  titleInAnchor,
  copytext,
  productItems,
  previewId,
  carousel = false,
  position,
  displayOption,
  border,
  backgroundColor,
  colorVariant,
  type,
  isWebshop = false,
  isChild = false,
}: Readonly<ProductTilesProps>) {
  const isXy = useXy();
  const isDesktop = useIsDesktop();
  const isLargeDesktop = useIsDesktop({ gdds: true });
  const isTablet = useIsTablet();
  const sectionId = useSectionId(title, anchor, titleInAnchor, contentIndex);
  const dispatch = useDispatch();
  const products = useSelector((state) =>
    productsByProductKeysCatalogSelector(state, productItems),
  );
  const isFetching = useSelector((state) =>
    isSectionLoadedBySectionIdSelector(state, hashCode(previewId)),
  );

  const catalog: string | null = useSelector(currentCatalogSelector);
  const bgColorClassName = backgroundColor ? backgroundColor : colorVariant;
  const textClassName = bgColorClassName === 'blue-grey' ? styles.textWhite : '';
  const onePerRowCenterClassName = position === 'center' ? styles.centered : '';
  const centeredClassName = products.length === 3 && isDesktop ? styles.centered : '';
  const isCarousel = checkIsCarousel(
    carousel,
    displayOption,
    products,
    isTablet,
    isDesktop,
    isLargeDesktop,
  );

  // Product tiles does not have the closing line / border parameter only the nordic one.
  const showClosingLine = border === '' && type !== 'product_tiles';

  // Default, if no displayOption is set, use for normal output not in carousel 4, 4, 3.
  const colSizesMap = {
    '1-per-row': [4, 4, 5],
    '2-per-row': [4, 4, 6],
    '3-per-row': [4, 4, 4],
    '4-per-row': [4, 4, 3],
  };
  const colSizes = displayOption ? (colSizesMap[displayOption] ?? [4, 4, 3]) : [4, 4, 3];

  useEffect(() => {
    if (!isEmpty(productItems)) {
      let sortedProductItems = {};

      productItems?.forEach((item) => {
        if (catalog) {
          let lang: string = catalog;
          let currentKey = item;

          if (/:/.test(item)) {
            [currentKey, lang] = item.split(':');
          }

          const keys = sortedProductItems[lang]
            ? [...sortedProductItems[lang], currentKey]
            : [currentKey];

          sortedProductItems = {
            ...sortedProductItems,
            [lang]: keys,
          };
        }
      });

      Object.entries(sortedProductItems).forEach(([catalogLanguage, entry]) => {
        dispatch(fetchProducts(entry as string[], previewId, catalogLanguage));
      });
    }
  }, [catalog, dispatch, previewId, productItems]);

  const renderCarousel = (children) => {
    if (isFetching && isCarousel) return;

    if (isCarousel) {
      return (
        <Col
          className={styles.carouselWrapper}
          size={[4, 8, isDesktop && !isLargeDesktop ? 10 : 12]}
        >
          <CarouselSSRWrapper
            responsiveLayout={{
              small: { slides: 1, slidesToMove: 1 },
              medium: { slides: 2, slidesToMove: 1 },
              large: { slides: 3, slidesToMove: 1 },
              xlarge: { slides: 4, slidesToMove: 1 },
            }}
            hideButtons
            supportMouse
            hideButtonsTablet
          >
            {children}
          </CarouselSSRWrapper>
        </Col>
      );
    }

    return children;
  };

  return (
    <section
      id={sectionId}
      className={buildClassName(styles.productTilesWrapper, styles[bgColorClassName])}
    >
      {(title || subtitle || copytext) && (
        <Container maxContentWidth={buildSize(gridSizes.gddsFullGrid)}>
          <Row>
            <Col className={styles.headlineCol} size={[4, 8, 8]}>
              {(title || subtitle) && (
                <Headline
                  title={title}
                  subtitle={subtitle}
                  tag={isChild && isXy ? Formats.h4 : Formats.h2}
                  variant={Formats.h2}
                  className={textClassName}
                  isFlexItem
                />
              )}
              {copytext && (
                <InnerHtml
                  as="p"
                  content={copytext}
                  className={buildClassName(styles.copyText, title || subtitle, textClassName)}
                  isGdds
                />
              )}
            </Col>
          </Row>
        </Container>
      )}
      <Container
        maxContentWidth={buildSize(
          gridSizes[isCarousel ? themeToGridSize.NORD_CAROUSEL : 'gddsFullGrid'],
        )}
      >
        <Row className={buildClassName(styles.negativeMargin, centeredClassName)}>
          {isDesktop && !isCarousel && products.length <= 2 && <Col size={[4, 8, 2]} />}
          {isDesktop && isCarousel && <Col size={[4, 8, 1]} />}
          {!isEmpty(products) &&
            renderCarousel(
              products.map((item) => {
                return (
                  <React.Fragment key={key(item)}>
                    {isCarousel ? (
                      <SingleTile
                        product={item}
                        linkType="product_detail_page"
                        isWebshop={isWebshop}
                        useProductTile
                      />
                    ) : displayOption === '1-per-row' ? (
                      <Col size={[12, 12, 12]}>
                        <Row className={onePerRowCenterClassName}>
                          <Col size={colSizes} className={styles.singleTileWrapper}>
                            <SingleTile
                              product={item}
                              linkType="product_detail_page"
                              isWebshop={isWebshop}
                              useProductTile
                            />
                          </Col>
                        </Row>
                      </Col>
                    ) : (
                      <Col size={colSizes} className={styles.singleTileWrapper}>
                        <SingleTile
                          product={item}
                          linkType="product_detail_page"
                          isWebshop={isWebshop}
                          useProductTile
                        />
                      </Col>
                    )}
                  </React.Fragment>
                );
              }),
            )}
        </Row>
      </Container>
      {showClosingLine && <SectionClosingLine />}
    </section>
  );
}
